import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Auth } from 'aws-amplify';
import { cognitoErrorMessage } from './cognito-errors';

// Hook compartilhavel pra logica de autenticacao com Cognito + Redux.
// Encapsula:
//   - chamada Auth.signIn
//   - dispatch USER/SAGA_SET_USER + USUARIO/SET_ERROR
//   - loading state
//   - rate limit cliente (cooldown apos N falhas)
//   - mensagem PT-BR via cognitoErrorMessage
//
// Por que existe: /login, /signup, /forgot-password, /reset-password
// chamam Auth.* do amplify e teriam logica duplicada (loading, erro,
// dispatch, rate limit). Centralizar aqui evita drift entre as telas
// quando a politica mudar (ex: threshold de cooldown, codigos novos).
//
// Uso minimo:
//   const { signIn, loading, error, cooldownLeftSec, inCooldown } = useAuth();
//   signIn(username, password)
//     .then(user => { ... })
//     .catch(() => { /* erro ja foi dispatchado; reagir e opcional */ });
//
// Retorna a Promise da Auth.signIn pra caller fazer roteamento proprio
// (NEW_PASSWORD_REQUIRED vai pra /signup, default vai pra /dashboard).
const RATE_LIMIT_THRESHOLD = 3;
const RATE_LIMIT_COOLDOWN_MS = 30 * 1000;

export const useAuth = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [cooldownUntil, setCooldownUntil] = useState(0);
  // tick state so pra forcar re-render a cada segundo durante cooldown.
  const [, setCooldownTick] = useState(0);
  const failCountRef = useRef(0);

  // Loop de tick + auto-clear quando cooldown expira. Roda so quando
  // cooldownUntil muda — quando vai pra 0 o cleanup limpa o interval.
  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const id = setInterval(() => {
      setCooldownTick((t) => t + 1);
      if (Date.now() >= cooldownUntil) {
        setCooldownUntil(0);
        failCountRef.current = 0;
      }
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const cooldownLeftSec = cooldownUntil
    ? Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000))
    : 0;
  const inCooldown = cooldownLeftSec > 0;

  const signIn = useCallback(
    (username, password) => {
      if (loading || inCooldown) {
        return Promise.reject(new Error('Throttled'));
      }
      setLoading(true);
      setLocalError('');
      return Auth.signIn(username, password)
        .then((user) => {
          dispatch({ type: 'USER/SAGA_SET_USER', user });
          dispatch({ type: 'USUARIO/SET_ERROR', erro: '' });
          failCountRef.current = 0;
          setLoading(false);
          return user;
        })
        .catch((e) => {
          const msg = cognitoErrorMessage(e);
          dispatch({ type: 'USUARIO/SET_ERROR', error: msg });
          setLocalError(msg);
          setLoading(false);
          failCountRef.current += 1;
          if (failCountRef.current >= RATE_LIMIT_THRESHOLD) {
            setCooldownUntil(Date.now() + RATE_LIMIT_COOLDOWN_MS);
          }
          throw e;
        });
    },
    [dispatch, loading, inCooldown],
  );

  return {
    signIn,
    loading,
    error: localError,
    cooldownLeftSec,
    inCooldown,
  };
};
